/* Experiences catalogue
   ---------------------
   Progressive enhancement over a page that is already complete without it.
   The markup ships every experience and every eSIM once, each carrying its
   facets as data attributes; this file reads those attributes, never the DOM
   text, and never calls the network.

   WHAT IT DOES NOT DO
   In `server` mode the catalogue has outgrown the browser and Shopify's own
   storefront filters are rendered instead. This file then stays out of the
   way entirely except for the drawer, the tabs and the WhatsApp greeting, so
   there is never a moment where the browser is filtering a page that only
   holds one page of results and reporting counts that are quietly wrong.

   PARAMETER NAMES ARE SHOPIFY'S
   The URL uses Shopify's storefront filter parameters in every mode. A link
   someone shares today keeps its meaning after the catalogue grows past
   `client_max` and Shopify starts answering the same URL server-side. */

(function () {
  'use strict';

  var root = document.querySelector('[data-xp]');
  if (!root) return;
  root.classList.remove('is-nojs');

  var MODE = root.getAttribute('data-xp-mode') || 'client';
  var CUR = root.getAttribute('data-xp-currency') || '';

  var P = {
    country: 'filter.p.m.experience.country',
    dest: 'filter.p.m.experience.destination',
    act: 'filter.p.m.experience.activity_types',
    instant: 'filter.p.m.experience.instant_confirmation',
    free: 'filter.p.m.experience.free_cancellation',
    gte: 'filter.v.price.gte',
    lte: 'filter.v.price.lte'
  };

  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };

  /* ------------------------------------------------------------ drawer */

  var drawer = $('[data-xp-drawer]');
  var drawerFoot = $('[data-xp-drawer-foot]');
  var scrim = $('[data-xp-scrim]');
  var openBtn = $('[data-xp-open]');
  var lastFocus = null;

  function trap(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
    var f = $$('a[href], button:not([disabled]), input, select, summary, [tabindex]:not([tabindex="-1"])', drawer)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openDrawer() {
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    if (drawerFoot) drawerFoot.classList.add('is-open');
    scrim.hidden = false;
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', trap);
    var close = $('[data-xp-close]', drawer);
    if (close) close.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    if (drawerFoot) drawerFoot.classList.remove('is-open');
    scrim.hidden = true;
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', trap);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  if (openBtn) openBtn.addEventListener('click', openDrawer);
  if (scrim) scrim.addEventListener('click', closeDrawer);
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-xp-close]')) closeDrawer();
  });

  /* --------------------------------------------------- WhatsApp greeting */

  /* The floating WhatsApp greeting comes from the Dondy app and its setting is
     store wide, so changing it there would change every page of the live site.
     This rewrites the prefilled message on this page only. Timed passes rather
     than a permanent body observer, because the app injects its widget after
     load and an observer on <body> would run for the life of the page. */
  function greeting() {
    $$('a[href*="wa.me"], a[href*="api.whatsapp.com"]', document).forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (href.indexOf('text=') === -1) return;
      var fixed = href.replace(/([?&]text=)[^&]*/, '$1Hello%2C%20I%20have%20a%20question');
      if (fixed !== href) a.setAttribute('href', fixed);
    });
  }
  [0, 900, 2500, 5000].forEach(function (d) { setTimeout(greeting, d); });

  /* ------------------------------------------------------------- tabs */

  var tabs = $$('[data-xp-tab]');
  var panels = $$('[data-xp-panel]');

  function setTab(name, push) {
    showTab(name);
    state.tab = name;
    syncUrl(push);
    render();
  }

  function showTab(name) {
    tabs.forEach(function (t) {
      t.setAttribute('aria-selected', String(t.getAttribute('data-xp-tab') === name));
    });
    panels.forEach(function (p) {
      var key = p.getAttribute('data-xp-panel');
      p.hidden = !(name === 'all' || name === key);
    });
    /* Panel headings are only useful when both panels are on screen at once.
       On a single-type tab the h1 and the tab already say what this is. */
    $$('[data-xp-panel-head]').forEach(function (h) { h.hidden = name !== 'all'; });
  }

  function wireTabs(onPick) {
    tabs.forEach(function (t) {
      t.addEventListener('click', function () { onPick(t.getAttribute('data-xp-tab')); });
      t.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(t), n = null;
        if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        if (!n) return;
        e.preventDefault();
        n.focus();
        onPick(n.getAttribute('data-xp-tab'));
      });
    });
  }

  function tabFromUrl() {
    var t = new URLSearchParams(location.search).get('tab') || 'all';
    return tabs.some(function (x) { return x.getAttribute('data-xp-tab') === t; }) ? t : 'all';
  }

  if (MODE === 'server') {
    /* Shopify owns the filtering here, so this file must not pretend to. It
       keeps the drawer, the tabs and the WhatsApp greeting and nothing else:
       filtering one page of results in the browser while reporting counts for
       the whole catalogue would be quietly wrong, which is worse than plain. */
    wireTabs(function (name) {
      showTab(name);
      var sp = new URLSearchParams(location.search);
      if (name === 'all') { sp.delete('tab'); } else { sp.set('tab', name); }
      var qs = sp.toString();
      try { history.pushState(null, '', location.pathname + (qs ? '?' + qs : '')); } catch (e) {}
    });
    showTab(tabFromUrl());
    return;
  }

  /* ------------------------------------------------------------- index */

  function read(el) {
    return {
      el: el,
      kind: el.getAttribute('data-kind'),
      country: el.getAttribute('data-country') || '',
      dest: el.getAttribute('data-dest') || '',
      act: el.getAttribute('data-act') || '||',
      /* Curated country names from connectivity.covers. Not tags, and not
         parsed out of the title: both lie. eSIM UK & Europe carries a
         Singapore tag, which is how a European plan was once recommended to
         someone looking at Singapore. */
      coversList: (el.getAttribute('data-covers') || '').split('|').filter(Boolean),
      dedicated: el.getAttribute('data-dedicated') === '1',
      instant: el.getAttribute('data-instant') === '1',
      free: el.getAttribute('data-free') === '1',
      /* 0 means "no price we can stand behind", not "free". It is kept
         distinct so it can sort last and be excluded from a price range
         rather than pretending to be the cheapest thing on the page. */
      price: parseFloat(el.getAttribute('data-price')) || 0,
      avail: el.getAttribute('data-avail') !== '0',
      created: parseInt(el.getAttribute('data-new'), 10) || 0,
      text: (el.getAttribute('data-text') || '').toLowerCase(),
      order: 0
    };
  }

  var expGrid = $('[data-xp-grid]');
  var esimGrid = $('[data-xp-grid-esim]');
  var esimRegionalGrid = $('[data-xp-grid-esim-regional]');

  var experiences = expGrid ? $$('.xp-item', expGrid).map(read) : [];
  var esims = esimGrid ? $$('.xp-item', esimGrid).map(read) : [];
  experiences.forEach(function (it, i) { it.order = i; });
  esims.forEach(function (it, i) { it.order = i; });

  /* Destination to country, learned from the cards rather than written down.
     It lets a search for "Tokyo" also match the Japan eSIMs without this file
     knowing that Tokyo is in Japan. */
  var destCountry = {};
  var countryLabel = {};
  experiences.forEach(function (it) {
    if (it.country) countryLabel[it.country.toLowerCase()] = it.country;
    if (it.dest && it.country) destCountry[it.dest.toLowerCase()] = it.country.toLowerCase();
  });

  /* A query that names exactly one place implies that country, so "tokyo"
     gets the same treatment as ticking Japan: the plans made for Japan lead
     and the multi-country passes that merely cover it go below. Without this
     a search would offer eSIM World as an equal answer to "tokyo". Only an
     unambiguous query counts; "asia" or two different countries leaves the
     eSIMs in the collection's own order rather than guessing. */
  function impliedCountry() {
    if (!qWords.length) return null;
    var hit = {};
    qWords.forEach(function (w) {
      var c = destCountry[w] || (countryLabel[w] ? w : null);
      if (c) hit[c] = true;
    });
    var keys = Object.keys(hit);
    return keys.length === 1 ? countryLabel[keys[0]] : null;
  }

  /* ------------------------------------------------------------- state */

  var state = { country: [], dest: [], act: [], instant: false, free: false, gte: '', lte: '', q: '', sort: 'manual', tab: 'all' };

  function readUrl() {
    var sp = new URLSearchParams(location.search);
    state.country = sp.getAll(P.country);
    state.dest = sp.getAll(P.dest);
    state.act = sp.getAll(P.act);
    state.instant = sp.get(P.instant) === '1';
    state.free = sp.get(P.free) === '1';
    state.gte = sp.get(P.gte) || '';
    state.lte = sp.get(P.lte) || '';
    state.q = sp.get('q') || '';
    state.sort = sp.get('sort_by') || 'manual';
    var t = sp.get('tab') || 'all';
    state.tab = tabs.some(function (x) { return x.getAttribute('data-xp-tab') === t; }) ? t : 'all';
  }

  function syncUrl(push) {
    var sp = new URLSearchParams();
    state.country.forEach(function (v) { sp.append(P.country, v); });
    state.dest.forEach(function (v) { sp.append(P.dest, v); });
    state.act.forEach(function (v) { sp.append(P.act, v); });
    if (state.instant) sp.set(P.instant, '1');
    if (state.free) sp.set(P.free, '1');
    if (state.gte) sp.set(P.gte, state.gte);
    if (state.lte) sp.set(P.lte, state.lte);
    if (state.q) sp.set('q', state.q);
    if (state.sort && state.sort !== 'manual') sp.set('sort_by', state.sort);
    if (state.tab && state.tab !== 'all') sp.set('tab', state.tab);
    var qs = sp.toString();
    var url = location.pathname + (qs ? '?' + qs : '');
    try {
      history[push ? 'pushState' : 'replaceState'](null, '', url);
    } catch (e) { /* file:// and some embedded views refuse; filtering still works */ }
  }

  /* ------------------------------------------------------------ matching */

  function inList(list, value) { return list.length === 0 || list.indexOf(value) !== -1; }

  function actMatches(item) {
    if (!state.act.length) return true;
    for (var i = 0; i < state.act.length; i++) {
      if (item.act.indexOf('|' + state.act[i] + '|') !== -1) return true;
    }
    return false;
  }

  function priceMatches(item) {
    if (!state.gte && !state.lte) return true;
    /* An unknown price cannot satisfy a range. Showing it anyway would put
       cards the visitor cannot judge inside a band they chose deliberately. */
    if (!item.price) return false;
    if (state.gte && item.price < parseFloat(state.gte)) return false;
    if (state.lte && item.price > parseFloat(state.lte)) return false;
    return true;
  }

  var qWords = [];
  function textMatches(item) {
    for (var i = 0; i < qWords.length; i++) {
      if (item.text.indexOf(qWords[i]) === -1) return false;
    }
    return true;
  }

  /* `skip` lets a facet's own selections be ignored, which is how a count can
     say how many results an option would give rather than how many it gives
     now, which is always zero for the options you did not tick. */
  function expMatches(item, skip) {
    if (skip !== 'country' && !inList(state.country, item.country)) return false;
    if (skip !== 'dest' && !inList(state.dest, item.dest)) return false;
    if (skip !== 'act' && state.act.length && !actMatches(item)) return false;
    if (skip !== 'booking') {
      if (state.instant && !item.instant) return false;
      if (state.free && !item.free) return false;
    }
    if (skip !== 'price' && !priceMatches(item)) return false;
    return textMatches(item);
  }

  /* eSIMs answer to the destination filters and the search box only.
     Activity type, instant confirmation, free cancellation and price are
     properties of an experience; letting them empty the connectivity panel
     would mean choosing "Theme parks" silently removed the SIM offer, which
     is not what the visitor asked for. */

  /* At most this many multi-country plans are offered as an alternative. They
     are a fallback, not a range: a wall of regional passes buries the plan
     actually made for where the visitor is going. */
  var REGIONAL_MAX = 3;

  /* The countries this catalogue actually sells experiences in, learned from
     the cards rather than configured. Add Thailand experiences and Thai eSIMs
     become eligible on their own, with no theme edit. */
  var launchCountries = [];
  experiences.forEach(function (it) {
    if (it.country && launchCountries.indexOf(it.country) === -1) launchCountries.push(it.country);
  });

  /* An eSIM is in scope only if it is dedicated to a country in scope, or has
     been curated as genuinely covering one. Nothing is inferred. */
  function esimInScope(item, scope) {
    for (var i = 0; i < scope.length; i++) {
      if (item.dedicated) {
        if (item.country === scope[i]) return true;
      } else if (item.coversList.indexOf(scope[i]) !== -1) {
        return true;
      }
    }
    return false;
  }

  function esimTextMatches(item) {
    if (!qWords.length) return true;
    for (var i = 0; i < qWords.length; i++) {
      var w = qWords[i];
      if (item.text.indexOf(w) !== -1) continue;
      /* "tokyo" should reach the Japan plans. */
      var c = destCountry[w];
      if (c && esimInScope(item, [countryLabel[c] || c])) continue;
      return false;
    }
    return true;
  }

  /* ------------------------------------------------------------- sorting */

  function sortItems(list) {
    var s = state.sort;
    var out = list.slice();
    out.sort(function (a, b) {
      /* Sold out sinks in every order. It is still findable, just never the
         first thing offered. */
      if (a.avail !== b.avail) return a.avail ? -1 : 1;
      if (s === 'price-ascending' || s === 'price-descending') {
        var ap = a.price, bp = b.price;
        if (!ap !== !bp) return ap ? -1 : 1;   /* unknown price last, both ways */
        if (ap !== bp) return s === 'price-ascending' ? ap - bp : bp - ap;
      } else if (s === 'created-descending') {
        if (a.created !== b.created) return b.created - a.created;
      }
      return a.order - b.order;
    });
    return out;
  }

  function reflow(grid, list) {
    if (!grid) return;
    var frag = document.createDocumentFragment();
    list.forEach(function (it) { frag.appendChild(it.el); });
    grid.appendChild(frag);
  }

  /* ------------------------------------------------------- facet groups */

  var GROUPS = [
    { key: 'country', param: P.country, label: 'Country', field: 'country', open: true },
    { key: 'dest', param: P.dest, label: 'Destination', field: 'dest', open: true },
    { key: 'act', param: P.act, label: 'Activity type', field: 'act', open: true }
  ];

  function valuesFor(g) {
    var seen = {};
    experiences.forEach(function (it) {
      if (g.key === 'act') {
        it.act.split('|').forEach(function (v) { if (v) seen[v] = true; });
      } else if (it[g.field]) {
        seen[it[g.field]] = true;
      }
    });
    return Object.keys(seen).sort(function (a, b) { return a.localeCompare(b); });
  }

  var groupsHost = $('[data-xp-groups]');
  var optionRows = {};   /* key -> { value -> {row, input, count} } */

  function buildGroups() {
    if (!groupsHost) return;
    GROUPS.forEach(function (g) {
      var vals = valuesFor(g);
      if (!vals.length) return;
      var details = document.createElement('details');
      details.className = 'xp-group';
      if (g.open) details.open = true;
      var summary = document.createElement('summary');
      var span = document.createElement('span');
      span.textContent = g.label;
      summary.appendChild(span);
      details.appendChild(summary);

      var body = document.createElement('div');
      body.className = 'xp-group-body';
      var ul = document.createElement('ul');
      ul.className = 'xp-opts';
      optionRows[g.key] = {};

      vals.forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'xp-opt-row';
        var label = document.createElement('label');
        label.className = 'xp-opt';
        var input = document.createElement('input');
        input.type = 'checkbox';
        input.name = g.param;
        input.value = v;
        input.checked = state[g.key].indexOf(v) !== -1;
        input.addEventListener('change', function () {
          var arr = state[g.key];
          var i = arr.indexOf(v);
          if (input.checked && i === -1) arr.push(v);
          if (!input.checked && i !== -1) arr.splice(i, 1);
          /* Choosing a country invalidates destinations from other countries;
             dropping them is less surprising than showing a filter that
             cannot match anything. */
          if (g.key === 'country' && state.country.length) {
            state.dest = state.dest.filter(function (d) {
              return experiences.some(function (it) {
                return it.dest === d && state.country.indexOf(it.country) !== -1;
              });
            });
          }
          syncUrl(true);
          render();
        });
        var box = document.createElement('span');
        box.className = 'xp-opt-box';
        box.setAttribute('aria-hidden', 'true');
        box.innerHTML = '<svg viewBox="0 0 24 24"><path d="m5 13 4 4L19 7"/></svg>';
        var text = document.createElement('span');
        text.className = 'xp-opt-label';
        text.textContent = v;
        var count = document.createElement('span');
        count.className = 'xp-opt-count';

        label.appendChild(input);
        label.appendChild(box);
        label.appendChild(text);
        label.appendChild(count);
        li.appendChild(label);
        ul.appendChild(li);
        optionRows[g.key][v] = { row: li, input: input, count: count };
      });

      body.appendChild(ul);
      details.appendChild(body);
      groupsHost.appendChild(details);
    });

    /* Booking group. Two booleans rather than a list, because "instant
       confirmation: no" is not something anyone filters for. */
    var bd = document.createElement('details');
    bd.className = 'xp-group';
    bd.open = true;
    bd.innerHTML = '<summary><span>Booking</span></summary>';
    var bbody = document.createElement('div');
    bbody.className = 'xp-group-body';
    var bul = document.createElement('ul');
    bul.className = 'xp-opts';
    optionRows.booking = {};
    [['instant', 'Instant confirmation', P.instant], ['free', 'Free cancellation', P.free]].forEach(function (t) {
      var li = document.createElement('li');
      li.className = 'xp-opt-row';
      li.innerHTML = '<label class="xp-opt"><input type="checkbox" name="' + t[2] + '" value="1">' +
        '<span class="xp-opt-box" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m5 13 4 4L19 7"/></svg></span>' +
        '<span class="xp-opt-label"></span><span class="xp-opt-count"></span></label>';
      li.querySelector('.xp-opt-label').textContent = t[1];
      var input = li.querySelector('input');
      input.checked = state[t[0]];
      input.addEventListener('change', function () {
        state[t[0]] = input.checked;
        syncUrl(true);
        render();
      });
      bul.appendChild(li);
      optionRows.booking[t[0]] = { row: li, input: input, count: li.querySelector('.xp-opt-count') };
    });
    bbody.appendChild(bul);
    bd.appendChild(bbody);
    groupsHost.appendChild(bd);

    /* Price */
    var pd = document.createElement('details');
    pd.className = 'xp-group';
    pd.open = true;
    pd.innerHTML = '<summary><span>Price</span></summary>';
    var pbody = document.createElement('div');
    pbody.className = 'xp-group-body';
    pbody.innerHTML =
      '<div class="xp-price">' +
      '<label><span class="visually-hidden">Minimum price</span><span class="xp-cur"></span>' +
      '<input type="number" inputmode="decimal" min="0" name="' + P.gte + '" placeholder="0" data-xp-gte></label>' +
      '<span class="xp-price-dash" aria-hidden="true">to</span>' +
      '<label><span class="visually-hidden">Maximum price</span><span class="xp-cur"></span>' +
      '<input type="number" inputmode="decimal" min="0" name="' + P.lte + '" data-xp-lte></label>' +
      '</div>';
    $$('.xp-cur', pbody).forEach(function (el) { el.textContent = CUR; });
    pd.appendChild(pbody);
    groupsHost.appendChild(pd);

    var gte = $('[data-xp-gte]', pbody), lte = $('[data-xp-lte]', pbody);
    var maxPrice = experiences.reduce(function (m, it) { return Math.max(m, it.price); }, 0);
    if (maxPrice) lte.placeholder = String(Math.ceil(maxPrice));
    gte.value = state.gte;
    lte.value = state.lte;
    var t;
    [gte, lte].forEach(function (el) {
      el.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          state.gte = gte.value.trim();
          state.lte = lte.value.trim();
          syncUrl(false);
          render();
        }, 250);
      });
    });
  }

  /* ------------------------------------------------------------- chips */

  var chipHost = $('[data-xp-chips]');
  var chips = {};

  function buildChips() {
    if (!chipHost) return;
    valuesFor(GROUPS[0]).forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'xp-chip';
      b.setAttribute('aria-pressed', 'false');
      var label = document.createElement('span');
      label.textContent = v;
      var n = document.createElement('span');
      n.className = 'xp-chip-n';
      b.appendChild(label);
      b.appendChild(n);
      b.addEventListener('click', function () {
        var i = state.country.indexOf(v);
        if (i === -1) state.country.push(v); else state.country.splice(i, 1);
        var box = optionRows.country && optionRows.country[v];
        if (box) box.input.checked = i === -1;
        syncUrl(true);
        render();
      });
      chipHost.appendChild(b);
      chips[v] = { el: b, n: n };
    });
  }

  /* ------------------------------------------------------------- search */

  var searchForm = $('[data-xp-search]');
  var searchInput = $('#xp-q');
  var clearQ = $('[data-xp-clear-q]');

  if (searchForm) {
    searchForm.addEventListener('submit', function (e) { e.preventDefault(); syncUrl(true); });
  }
  if (searchInput) {
    var st;
    searchInput.addEventListener('input', function () {
      clearTimeout(st);
      st = setTimeout(function () {
        state.q = searchInput.value.trim();
        syncUrl(false);
        render();
      }, 160);
    });
  }
  if (clearQ) {
    clearQ.addEventListener('click', function () {
      searchInput.value = '';
      state.q = '';
      syncUrl(true);
      render();
      searchInput.focus();
    });
  }

  /* --------------------------------------------------------------- sort */

  var sortSel = $('[data-xp-sort]');
  if (sortSel) {
    sortSel.addEventListener('change', function () {
      state.sort = sortSel.value;
      syncUrl(true);
      render();
    });
  }

  /* ------------------------------------------------------- clear / pills */

  function clearAll() {
    state.country = [];
    state.dest = [];
    state.act = [];
    state.instant = false;
    state.free = false;
    state.gte = '';
    state.lte = '';
    state.q = '';
    if (searchInput) searchInput.value = '';
    $$('[data-xp-form] input[type="checkbox"]').forEach(function (i) { i.checked = false; });
    $$('[data-xp-form] input[type="number"]').forEach(function (i) { i.value = ''; });
    syncUrl(true);
    render();
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-xp-clear-all]')) clearAll();
  });

  var activeBar = $('[data-xp-active]');
  var activeList = $('[data-xp-active-list]');

  function pill(text, onRemove) {
    var li = document.createElement('li');
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'xp-pill';
    var s = document.createElement('span');
    s.textContent = text;
    var hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = ', remove filter';
    b.appendChild(s);
    b.appendChild(hidden);
    b.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>');
    b.addEventListener('click', onRemove);
    li.appendChild(b);
    activeList.appendChild(li);
  }

  function renderPills() {
    activeList.innerHTML = '';
    var n = 0;
    ['country', 'dest', 'act'].forEach(function (key) {
      state[key].slice().forEach(function (v) {
        n++;
        pill(v, function () {
          var i = state[key].indexOf(v);
          if (i !== -1) state[key].splice(i, 1);
          var box = optionRows[key] && optionRows[key][v];
          if (box) box.input.checked = false;
          syncUrl(true);
          render();
        });
      });
    });
    if (state.instant) {
      n++;
      pill('Instant confirmation', function () {
        state.instant = false;
        if (optionRows.booking) optionRows.booking.instant.input.checked = false;
        syncUrl(true); render();
      });
    }
    if (state.free) {
      n++;
      pill('Free cancellation', function () {
        state.free = false;
        if (optionRows.booking) optionRows.booking.free.input.checked = false;
        syncUrl(true); render();
      });
    }
    if (state.gte || state.lte) {
      n++;
      pill(CUR + (state.gte || '0') + ' to ' + (state.lte ? CUR + state.lte : 'any'), function () {
        state.gte = ''; state.lte = '';
        $$('[data-xp-form] input[type="number"]').forEach(function (i) { i.value = ''; });
        syncUrl(true); render();
      });
    }
    if (state.q) {
      n++;
      pill('"' + state.q + '"', function () {
        state.q = '';
        if (searchInput) searchInput.value = '';
        syncUrl(true); render();
      });
    }
    activeBar.hidden = n === 0;
    var badge = $('[data-xp-badge]');
    if (badge) { badge.hidden = n === 0; badge.textContent = String(n); }
    if (clearQ) clearQ.hidden = !state.q;
    return n;
  }

  /* -------------------------------------------------------------- render */

  var countEl = $('[data-xp-count]');
  var drawerCount = $('[data-xp-drawer-count]');
  var esimTitle = $('[data-xp-esim-title]');
  var esimNote = $('[data-xp-esim-note]');
  var subDedicated = $('[data-xp-esim-sub-dedicated]');
  var subRegional = $('[data-xp-esim-sub-regional]');
  var emptyExp = $('[data-xp-empty="experiences"]');
  var emptyExpMsg = $('[data-xp-empty-msg]');
  var emptyEsim = $('[data-xp-empty="esims"]');

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  function render() {
    qWords = state.q ? state.q.toLowerCase().split(/\s+/).filter(Boolean) : [];

    var visExp = [];
    experiences.forEach(function (it) {
      var ok = expMatches(it, null);
      it.el.hidden = !ok;
      if (ok) visExp.push(it);
    });

    /* Default scope is every country the catalogue covers. Pick a country and
       the scope narrows to it. Either way the worldwide range never appears. */
    var scope = state.country.length ? state.country : launchCountries;
    var pool = esims.filter(function (it) {
      return esimInScope(it, scope) && esimTextMatches(it);
    });
    var dedicated = sortItems(pool.filter(function (i) { return i.dedicated; }));
    var regional = sortItems(pool.filter(function (i) { return !i.dedicated; }))
      .slice(0, REGIONAL_MAX);
    var visEsim = dedicated.concat(regional);
    var shown = {};
    visEsim.forEach(function (i) { shown[i.order] = true; });
    esims.forEach(function (it) { it.el.hidden = !shown[it.order]; });

    reflow(expGrid, sortItems(visExp).concat(experiences.filter(function (i) { return i.el.hidden; })));

    /* eSIMs: when exactly one country is chosen, the plans made for it come
       first and the multi-country plans that merely cover it go below, under
       their own heading. Without a country there is nothing to be dedicated
       to, so they stay in the collection's own order. */
    var one = state.country.length === 1 ? state.country[0] : impliedCountry();
    var hiddenEsims = esims.filter(function (i) { return i.el.hidden; });
    if (esimGrid) {
      /* Hidden cards ride with the dedicated grid so none is ever left behind
         in a group that is about to be collapsed. */
      reflow(esimGrid, dedicated.concat(hiddenEsims));
      if (esimRegionalGrid) {
        reflow(esimRegionalGrid, regional);
        esimRegionalGrid.hidden = regional.length === 0;
      }
      if (subDedicated) {
        subDedicated.hidden = dedicated.length === 0;
        subDedicated.textContent = one ? 'Made for ' + one : 'Made for one destination';
      }
      if (subRegional) {
        subRegional.hidden = regional.length === 0;
        subRegional.textContent = one
          ? 'Multi-country plans that also cover ' + one
          : 'Multi-country alternatives';
      }
      if (esimTitle) esimTitle.textContent = one ? 'Stay connected in ' + one : 'Stay connected';
      if (esimNote) {
        if (!one) {
          esimNote.textContent = 'Data plans for the countries this catalogue covers.';
        } else if (dedicated.length) {
          esimNote.textContent = 'Data plans made for ' + one + ', not regional passes that happen to cover it.';
        } else {
          /* Nothing is dedicated to this country yet, so promising a plan made
             for it would be a claim the cards below cannot support. */
          esimNote.textContent = 'No plan is dedicated to ' + one + ' yet. These multi-country plans cover it.';
        }
      }
    }

    /* Counts on the facet options: how many results this option WOULD give,
       computed against every other active filter, which is why a group's own
       selections are skipped. */
    GROUPS.forEach(function (g) {
      var rows = optionRows[g.key];
      if (!rows) return;
      var pool = experiences.filter(function (it) { return expMatches(it, g.key); });
      Object.keys(rows).forEach(function (v) {
        var n = pool.filter(function (it) {
          return g.key === 'act' ? it.act.indexOf('|' + v + '|') !== -1 : it[g.field] === v;
        }).length;
        rows[v].count.textContent = String(n);
        rows[v].row.hidden = n === 0 && !rows[v].input.checked;
      });
    });
    if (optionRows.booking) {
      var bpool = experiences.filter(function (it) { return expMatches(it, 'booking'); });
      optionRows.booking.instant.count.textContent = String(bpool.filter(function (i) { return i.instant; }).length);
      optionRows.booking.free.count.textContent = String(bpool.filter(function (i) { return i.free; }).length);
    }

    var chipPool = experiences.filter(function (it) { return expMatches(it, 'country'); });
    Object.keys(chips).forEach(function (v) {
      chips[v].el.setAttribute('aria-pressed', String(state.country.indexOf(v) !== -1));
      chips[v].n.textContent = String(chipPool.filter(function (it) { return it.country === v; }).length);
    });

    var nFilters = renderPills();

    var tabCounts = { all: visExp.length + visEsim.length, experiences: visExp.length, esims: visEsim.length };
    Object.keys(tabCounts).forEach(function (k) {
      var el = $('[data-xp-tabcount="' + k + '"]');
      if (el) el.textContent = String(tabCounts[k]);
    });

    var showing = tabCounts[state.tab];
    if (countEl) {
      countEl.textContent = state.tab === 'esims'
        ? plural(showing, 'eSIM', 'eSIMs')
        : (state.tab === 'experiences' ? plural(showing, 'experience', 'experiences') : plural(showing, 'result', 'results'));
    }
    if (drawerCount) drawerCount.textContent = String(showing);

    if (emptyExp) {
      emptyExp.hidden = visExp.length > 0;
      if (emptyExpMsg) {
        emptyExpMsg.textContent = nFilters > 0
          ? 'Try removing one filter, or choosing a different destination.'
          : 'Experiences are being added. Please check back shortly.';
      }
      /* Nothing to clear means the button is a dead end, so it goes away. */
      var btn = $('[data-xp-clear-all]', emptyExp);
      if (btn) btn.hidden = nFilters === 0;
    }
    if (emptyEsim) emptyEsim.hidden = visEsim.length > 0;
  }

  /* --------------------------------------------------------------- boot */

  readUrl();
  buildGroups();
  buildChips();
  if (searchInput) searchInput.value = state.q;
  if (sortSel) sortSel.value = state.sort;
  wireTabs(function (name) { setTab(name, true); });
  setTab(state.tab, false);

  window.addEventListener('popstate', function () {
    readUrl();
    if (searchInput) searchInput.value = state.q;
    if (sortSel) sortSel.value = state.sort;
    GROUPS.forEach(function (g) {
      var rows = optionRows[g.key];
      if (!rows) return;
      Object.keys(rows).forEach(function (v) { rows[v].input.checked = state[g.key].indexOf(v) !== -1; });
    });
    if (optionRows.booking) {
      optionRows.booking.instant.input.checked = state.instant;
      optionRows.booking.free.input.checked = state.free;
    }
    var gte = $('[data-xp-gte]'), lte = $('[data-xp-lte]');
    if (gte) gte.value = state.gte;
    if (lte) lte.value = state.lte;
    setTab(state.tab, false);
  });
})();
